// src/components/operationCenter/widgets/token/TokenWidgetConfig.tsx
// Shared config panel for token dashboard widgets.
// Uses live token search (useTokenSearch) so users pick from real available tokens.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import type { TokenWidgetConfig, WidgetType } from "@/types/dashboard";
import { useTokenSearch } from "@/hooks/useTokenSearch";
import { WidgetHeightContext } from "@/contexts/widgetHeightContext";
import { getWidgetDefinition } from "../index";

interface TokenWidgetConfigPanelProps {
  config: TokenWidgetConfig;
  onConfigChange: ((config: Partial<TokenWidgetConfig>) => void) | undefined;
  widgetType?: WidgetType;
  children: React.ReactNode;
}

/**
 * Wraps children with a settings header + collapsible token search config panel.
 * Users search and pick from real available tokens — no free-text entry.
 */
export const TokenWidgetConfigPanel: React.FC<TokenWidgetConfigPanelProps> = ({
  config,
  onConfigChange,
  widgetType,
  children,
}) => {
  const widgetLabel = widgetType
    ? getWidgetDefinition(widgetType)?.label
    : undefined;
  const [showConfig, setShowConfig] = useState(!config.ticker);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Measure content area so charts can fill the widget height
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (el.clientHeight > 0) setContentHeight(el.clientHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { results, isLoading } = useTokenSearch(query);

  // Focus search input when config panel opens
  useEffect(() => {
    if (showConfig) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [showConfig]);

  const handleSelect = useCallback(
    (ticker: string) => {
      onConfigChange?.({ ticker: ticker.toUpperCase() });
      setQuery("");
      setShowConfig(false);
    },
    [onConfigChange],
  );

  const handleCancel = useCallback(() => {
    setQuery("");
    setShowConfig(false);
  }, []);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header — matches WidgetWrapper standard header style */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
        <div className="flex-1 text-xs uppercase tracking-wide text-slate-300 truncate">
          {widgetLabel ?? "Token Widget"}
        </div>
        <button
          onClick={() => setShowConfig((s) => !s)}
          className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium transition-colors flex-shrink-0 ${
            config.ticker
              ? "bg-blue-500/15 text-blue-400 hover:bg-blue-500/25 hover:text-blue-300 border border-blue-500/30"
              : "bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 hover:text-amber-300 border border-amber-500/30"
          }`}
          title="Change token"
        >
          {config.ticker || "Select"}
          <ChevronDown className={`w-3 h-3 transition-transform ${showConfig ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Config panel */}
      {showConfig && (
        <div className="flex-shrink-0 border-b border-slate-700/30 bg-gray-900/50">
          {/* Search input */}
          <div className="p-2">
            <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1">
              Select Token
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tokens…"
                className="w-full pl-8 pr-8 py-1.5 text-xs bg-gray-800/60 border border-gray-700/50 rounded-md text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Results dropdown */}
          <div className="max-h-48 overflow-y-auto border-t border-slate-700/30">
            {query.length < 2 ? (
              <p className="px-3 py-2 text-[11px] text-slate-500">
                Type at least 2 characters to search
              </p>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-3">
                <div className="loading loading-spinner loading-xs text-slate-400" />
              </div>
            ) : results.length === 0 ? (
              <p className="px-3 py-2 text-[11px] text-slate-500">No tokens found</p>
            ) : (
              results.map((token) => (
                <button
                  key={token.ticker}
                  type="button"
                  onClick={() => handleSelect(token.ticker)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-700/60 transition-colors"
                >
                  {token.logo_url ? (
                    <img
                      src={token.logo_url}
                      alt={token.ticker}
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-slate-600 flex-shrink-0" />
                  )}
                  <span className="font-medium text-slate-200">{token.ticker}</span>
                  {token.name && (
                    <span className="text-slate-500 truncate">{token.name}</span>
                  )}
                </button>
              ))
            )}
          </div>

          {/* Cancel */}
          {config.ticker && (
            <div className="flex justify-end px-2 pb-2">
              <button
                onClick={handleCancel}
                className="btn btn-xs btn-ghost text-slate-400"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* Widget content — overflow-y-auto allows scroll; charts fill available height via context */}
      <WidgetHeightContext.Provider value={contentHeight}>
        <div ref={contentRef} className="flex-1 min-h-0 overflow-hidden">
          {children}
        </div>
      </WidgetHeightContext.Provider>
    </div>
  );
};
